/**
 * CEO Executive Summary block.
 *
 * Implements the spec the user provided — a high-level, strategic
 * CEO view, NOT operational details. Four sections:
 *
 *   1. 8 Top Summary Cards   — Total Revenue · Sales Target % ·
 *                              Total Bookings · Conversion · Active
 *                              Leads · Collections · Receivables ·
 *                              Top Project
 *   2. Quick Alerts          — High-value leads pending · Overdue
 *                              follow-ups · Low inventory ·
 *                              Projects below target
 *   3. Financial Snapshot    — Collections · Pending · Outstanding
 *                              · MTD revenue
 *   4. Customer Insights     — CSAT score · Open tickets ·
 *                              Cancellation rate
 *
 * Backed by ONE call to /api/leadership/executive-summary so the
 * block renders together. Signals from collections that may not be
 * wired in this org (csat_scores, support_tickets, payments) show
 * "—" with a "Not connected" hint instead of fake zeros.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Wallet, Target, ClipboardCheck, PercentCircle, Users,
  Banknote, AlertTriangle, Star, Bell, TrendingDown,
  Building2, Trophy, Smile, MessageSquareWarning, RotateCcw,
  ArrowUpRight, SlidersHorizontal,
} from 'lucide-react';
import HelpInfo from '@/components/HelpInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtAED = (n) => {
  if (n == null || Number.isNaN(Number(n))) return 'AED 0';
  const v = Math.round(Number(n));
  if (Math.abs(v) >= 1_000_000) return `AED ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `AED ${(v / 1_000).toFixed(1)}K`;
  return `AED ${v.toLocaleString('en-AE')}`;
};

const fmtInt = (n) => (n == null ? '0' : Number(n).toLocaleString('en-AE'));

// ============================================================
// 8 Top Summary Cards
// ============================================================
const SUMMARY_CARDS = [
  // Each card spec — icon, label, value-builder, sub-builder, accent.
  // Centralised so the JSX stays compact and re-ordering is trivial.
  {
    key: 'total_revenue',
    icon: Wallet,
    label: 'Total Revenue',
    accent: 'emerald',
    value: (d) => fmtAED(d?.total_revenue),
    sub:   () => 'All-time, closed-won',
  },
  {
    key: 'target_achievement_pct',
    icon: Target,
    label: 'Target Achievement',
    accent: 'violet',
    value: (d) => (d?.target_achievement_pct != null
      ? `${d.target_achievement_pct}%`
      : '—'),
    sub:   (d) => (d?.target_set
      ? `vs YTD target ${fmtAED(d?.ytd_target)}`
      : 'Set weekly target to enable'),
  },
  {
    key: 'total_bookings',
    icon: ClipboardCheck,
    label: 'Total Bookings',
    accent: 'blue',
    value: (d) => fmtInt(d?.total_bookings),
    sub:   () => 'Closed-won deals',
  },
  {
    key: 'conversion_pct',
    icon: PercentCircle,
    label: 'Conversion Rate',
    accent: 'amber',
    value: (d) => `${(d?.conversion_pct ?? 0).toFixed(1)}%`,
    sub:   () => 'YTD inquiries → wins',
  },
  {
    key: 'active_leads',
    icon: Users,
    label: 'Active Leads',
    accent: 'rose',
    value: (d) => fmtInt(d?.active_leads),
    sub:   () => 'Currently in pursuit',
  },
  {
    key: 'collections_received',
    icon: Banknote,
    label: 'Collections Received',
    accent: 'emerald',
    value: (d) => fmtAED(d?.collections_received),
    sub:   (d) => (d?.estimates ? 'Estimate · 30% of revenue' : 'Cleared to date'),
  },
  {
    key: 'pending_receivables',
    icon: AlertTriangle,
    label: 'Pending Receivables',
    accent: 'rose',
    value: (d) => fmtAED(d?.pending_receivables),
    sub:   (d) => (d?.estimates ? 'Estimate · 70% of revenue' : 'Outstanding instalments'),
  },
  {
    key: 'top_project',
    icon: Trophy,
    label: 'Top Project',
    accent: 'amber',
    value: (d) => (d?.top_project ? d.top_project.name : '—'),
    sub:   (d) => (d?.top_project
      ? `${d.top_project.deals} deals · ${fmtAED(d.top_project.revenue)}`
      : 'No closed deals yet'),
  },
];

const ACCENT = {
  emerald: { fg: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-100' },
  violet:  { fg: 'text-violet-600',  bg: 'bg-violet-50',  ring: 'ring-violet-100'  },
  blue:    { fg: 'text-blue-600',    bg: 'bg-blue-50',    ring: 'ring-blue-100'    },
  amber:   { fg: 'text-amber-600',   bg: 'bg-amber-50',   ring: 'ring-amber-100'   },
  rose:    { fg: 'text-rose-600',    bg: 'bg-rose-50',    ring: 'ring-rose-100'    },
};

const SummaryCard = ({ spec, data }) => {
  const Icon = spec.icon;
  const tone = ACCENT[spec.accent] || ACCENT.emerald;
  return (
    <Card className="p-4 border-slate-200 hover:shadow-md transition relative overflow-hidden">
      <div className={`absolute -top-6 -right-6 w-24 h-24 rounded-full ${tone.bg} opacity-50 blur-2xl`} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg ${tone.bg} ${tone.fg} flex items-center justify-center ring-1 ${tone.ring}`}>
            <Icon size={14} />
          </div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{spec.label}</p>
        </div>
        <p className="text-xl font-bold text-slate-900 tracking-tight tabular-nums truncate">
          {spec.value(data)}
        </p>
        <p className="text-[11px] text-slate-500 mt-1 truncate">{spec.sub(data)}</p>
      </div>
    </Card>
  );
};

// ============================================================
// Quick Alerts strip
// ============================================================
const ALERT_SPECS = [
  {
    key: 'high_value_pending',
    icon: Star,
    label: 'High-Value Leads Pending',
    tone: 'amber',
    pick: (d) => d?.high_value_pending,
    sub:  (d) => `Above ${fmtAED(d?.high_value_threshold)}`,
  },
  {
    key: 'overdue_followups',
    icon: Bell,
    label: 'Overdue Follow-ups',
    tone: 'rose',
    pick: (d) => d?.overdue_followups,
    sub:  () => 'Buyers slipping',
  },
  {
    key: 'low_inventory_projects',
    icon: Building2,
    label: 'Inventory Running Low',
    tone: 'orange',
    pick: (d) => d?.low_inventory_projects,
    sub:  () => 'Projects with <5 units',
  },
  {
    key: 'projects_below_target',
    icon: TrendingDown,
    label: 'Projects Below Target',
    tone: 'slate',
    pick: (d) => d?.projects_below_target,
    sub:  () => 'Underperforming this YTD',
  },
];

const ALERT_TONE = {
  amber:  'from-amber-50 to-amber-100/40 border-amber-200 text-amber-700',
  rose:   'from-rose-50 to-rose-100/40 border-rose-200 text-rose-700',
  orange: 'from-orange-50 to-orange-100/40 border-orange-200 text-orange-700',
  slate:  'from-slate-50 to-slate-100/40 border-slate-200 text-slate-700',
};

const QuickAlerts = ({ data }) => (
  <Card className="p-5 border-slate-200">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
        <Bell size={16} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Quick Alerts</h3>
        <p className="text-[11px] text-slate-500">Where to look this week</p>
      </div>
      <HelpInfo
        align="inline"
        title="Quick Alerts"
        steps={[
          <><span className="font-medium">High-Value Leads Pending</span> — open buyers with deal_value &ge; AED 2M. Push them first; ticket size justifies the attention.</>,
          <><span className="font-medium">Overdue Follow-ups</span> — buyers waiting on the agent\'s next call.</>,
          <><span className="font-medium">Inventory Running Low</span> — projects with fewer than 5 available units. Plan the next launch.</>,
          <><span className="font-medium">Projects Below Target</span> — projects whose YTD revenue sits well below the leader.</>,
        ]}
        tip="A green dot doesn't mean nothing is wrong — it means none of those four signals tripped. Cross-check with Today's Schedule before assuming quiet."
      />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {ALERT_SPECS.map((spec) => {
        const v = spec.pick(data);
        const Icon = spec.icon;
        const dim = v == null || v === 0;
        return (
          <div key={spec.key}
               className={`relative rounded-xl p-3 border bg-gradient-to-br ${ALERT_TONE[spec.tone]} ${dim ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between">
              <Icon size={15} />
              <span className="text-2xl font-bold tabular-nums">{fmtInt(v ?? 0)}</span>
            </div>
            <p className="text-[12px] font-semibold mt-2">{spec.label}</p>
            <p className="text-[10px] opacity-80 mt-0.5">{spec.sub(data)}</p>
          </div>
        );
      })}
    </div>
  </Card>
);

// ============================================================
// Financial Snapshot + Customer Insights row
// ============================================================
const FinancialRow = ({ icon: Icon, label, value, sub, isEstimate, accent = 'emerald' }) => {
  const tone = ACCENT[accent] || ACCENT.emerald;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className={`w-9 h-9 rounded-lg ${tone.bg} ${tone.fg} flex items-center justify-center shrink-0`}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <p className="text-base font-bold text-slate-900 tabular-nums">{value}</p>
        {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
      </div>
      {isEstimate && (
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold border border-amber-200">
          Estimate
        </span>
      )}
    </div>
  );
};

const FinancialSnapshot = ({ data }) => (
  <Card className="p-5 border-slate-200 h-full">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
        <Banknote size={16} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Financial Snapshot</h3>
        <p className="text-[11px] text-slate-500">Cash position at a glance</p>
      </div>
      <HelpInfo
        align="inline"
        title="Financial Snapshot"
        steps={[
          'Collections Received = total cash cleared from booked deals.',
          'Pending Payments = next instalments billed but not yet received.',
          'Outstanding Receivables = balance owed across closed deals.',
          'MTD Revenue = closed-won deals this month (booking value, not cash).',
        ]}
        tip="The 'Estimate' tag shows when the system is approximating from booking value because a real payments collection isn't wired yet. Connect your payments source to replace the estimate."
      />
    </div>
    {!data ? (
      <div className="space-y-2">
        {[0,1,2,3].map((i) => <Skeleton key={i} className="h-14" />)}
      </div>
    ) : (
      <div>
        <FinancialRow
          icon={Banknote}
          label="Collections Received"
          value={fmtAED(data.collections_received)}
          sub="Cleared to date"
          isEstimate={data.is_estimate}
          accent="emerald"
        />
        <FinancialRow
          icon={AlertTriangle}
          label="Pending Payments"
          value={fmtAED(data.pending_payments)}
          sub="Next instalments due"
          isEstimate={data.is_estimate}
          accent="amber"
        />
        <FinancialRow
          icon={ArrowUpRight}
          label="Outstanding Receivables"
          value={fmtAED(data.outstanding_receivables)}
          sub="Balance across closed deals"
          isEstimate={data.is_estimate}
          accent="rose"
        />
        <FinancialRow
          icon={Wallet}
          label="Revenue Closed (MTD)"
          value={fmtAED(data.mtd_revenue)}
          sub="Booking value this month"
          accent="violet"
        />
      </div>
    )}
  </Card>
);

// ============================================================
// Customer Insights
// ============================================================
const CustomerInsights = ({ data }) => (
  <Card className="p-5 border-slate-200 h-full">
    <div className="flex items-center gap-2 mb-3">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
        <Smile size={16} />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Customer Insights</h3>
        <p className="text-[11px] text-slate-500">How buyers feel, post-booking</p>
      </div>
      <HelpInfo
        align="inline"
        title="Customer Insights"
        steps={[
          'CSAT Score — average satisfaction rating from post-booking surveys.',
          'Open Tickets — unresolved support / handover issues.',
          'Cancellation Rate — % of bookings flipped to cancelled.',
        ]}
        tip="CSAT and Open Tickets show '—' until the survey / support collections are wired. A high Cancellation Rate alongside healthy bookings is the strongest signal of a post-sale handover problem."
      />
    </div>
    {!data ? (
      <div className="space-y-2">
        {[0,1,2].map((i) => <Skeleton key={i} className="h-14" />)}
      </div>
    ) : (
      <div className="space-y-3">
        <div className="rounded-xl p-3 bg-gradient-to-br from-emerald-50 to-white border border-emerald-100">
          <div className="flex items-center gap-2">
            <Smile size={14} className="text-emerald-600" />
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">CSAT Score</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {data.csat_connected ? `${data.csat_score} / 5` : '—'}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {data.csat_connected ? 'Average post-booking rating' : 'Connect CSAT survey to enable'}
          </p>
        </div>
        <div className="rounded-xl p-3 bg-gradient-to-br from-amber-50 to-white border border-amber-100">
          <div className="flex items-center gap-2">
            <MessageSquareWarning size={14} className="text-amber-600" />
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Open Tickets</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">
            {data.tickets_connected ? fmtInt(data.open_tickets) : '—'}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {data.tickets_connected ? 'Awaiting resolution' : 'Connect support inbox to enable'}
          </p>
        </div>
        <div className="rounded-xl p-3 bg-gradient-to-br from-rose-50 to-white border border-rose-100">
          <div className="flex items-center gap-2">
            <RotateCcw size={14} className="text-rose-600" />
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Cancellation Rate</p>
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{(data.cancellation_rate ?? 0).toFixed(1)}%</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {fmtInt(data.cancelled_count)} cancelled · {fmtInt(data.total_bookings)} bookings
          </p>
        </div>
      </div>
    )}
  </Card>
);

// ============================================================
// Main block
// ============================================================
export default function CEOExecutiveSummary() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/leadership/executive-summary`, {
        params: { token },
      });
      setData(r.data?.data || null);
    } catch (e) {
      console.warn('executive-summary:', e?.response?.data?.detail || e);
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const cards     = data?.summary_cards;
  const alerts    = data?.quick_alerts;
  const financial = data?.financial;
  const customer  = data?.customer;

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div className="flex items-center justify-between px-1">
        <div>
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Trophy size={18} className="text-amber-500" />
            Executive Summary
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Eight strategic cards · quick alerts · financial snapshot · customer signals
          </p>
        </div>
        <button
          className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 transition"
          title="Customise summary"
        >
          <SlidersHorizontal size={13} />
        </button>
      </div>

      {/* 8 Top Summary Cards — 4 across on lg, 2 on md, 1 on sm */}
      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {SUMMARY_CARDS.map((spec) => (
            <SummaryCard key={spec.key} spec={spec} data={cards} />
          ))}
        </div>
      )}

      {/* Quick Alerts */}
      <QuickAlerts data={alerts || {}} />

      {/* Financial Snapshot + Customer Insights side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FinancialSnapshot data={financial} />
        <CustomerInsights  data={customer}  />
      </div>
    </div>
  );
}
